"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import Link from "next/link";

import { Countdown } from "@/features/home-v2/live";
import { Creator, TILE_PLACEHOLDER, chainName } from "@/features/home-v2/tiles";
import { parseDropDateTime } from "../permissions";
import type { Drop } from "../types";
import { dropFigures } from "../dropFigures";
import { pricePartsOf } from "./itemFacts";
import "./drop-tile.css";
import { withPercent } from "@/lib/percent";

type Props = { drop: Drop; lang: "fa" | "en"; sizes?: string };

/** Under ten days to go, the start is a ticking countdown; further out, a date. */
const COUNTDOWN_WINDOW = 10 * 86_400_000;

/**
 * A drop, as a v2 tile — the legacy DropCard's facts in the tile grammar:
 * the banner with the phase badge; the title, the creator and the chain;
 * price and supply; and a status line at the foot: the mint bar when both
 * minted and supply are real numbers (an unknown count is never drawn as 0%,
 * see types.ts), otherwise when it starts or ended — a live countdown when
 * the start is under ten days away, as the legacy card had. A null price
 * reads as a dash, never "free".
 */
export default function DropTile({ drop, lang, sizes = "(max-width: 619px) 50vw, 240px" }: Props) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => (isFa ? fa : en);
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  // Whole-drop figures: the backend's summary, or the items when all of them are here (dropFigures).
  const fig = dropFigures(drop);
  // Open in time but nothing left: "sold out", never "minting"; and no item at all: "no items yet".
  const phase = drop.status === "minting" ? (fig.empty ? "empty" : fig.soldOut ? "soldout" : "live") : drop.status === "ended" ? "ended" : drop.status === "upcoming" ? "soon" : null;
  const progress = fig.supply !== null && fig.minted !== null ? Math.min(100, (fig.minted / fig.supply) * 100) : null;
  const day = (iso: string | null) => {
    const d = iso ? new Date(iso.replace(" ", "T")) : null;
    return d && !Number.isNaN(d.getTime()) ? new Intl.DateTimeFormat(isFa ? "fa-IR" : "en-US", { month: "short", day: "numeric" }).format(d) : "";
  };
  // With the items or the summary (the /drops list): "from" price and how many
  // items. Without (saved, search): what the drop itself states — royalty and when it went public.
  const items = drop.items ?? [];
  const known = items.length > 0 || Boolean(drop.summary);
  const price = known ? pricePartsOf(fig.range, fig.lowest, nf) : null;
  const royalty = drop.meta_data?.owner_has_model?.royalty_percent ?? null;
  const facts: { k: string; v: ReactNode; unit?: string }[] = known
    ? [{ k: t("قیمت از", "From"), v: price ? price.map((x, i) => <span key={x.symbol} className="v2-drop-part">{i > 0 ? <i aria-hidden>·</i> : null}<bdi>{x.text}</bdi><small>{x.symbol}</small></span>) : "—" }, { k: t("آیتم‌ها", "Items"), v: nf.format(fig.itemsCount) }]
    : [{ k: t("رویالتی", "Royalty"), v: royalty !== null ? withPercent(nf.format(royalty), isFa) : "—" }, { k: t("انتشار", "Published"), v: day(drop.publishedAt ?? null) || "—" }];
  const foot: { state: "minting" | "soon" | "ended" | "unknown"; label: string; value: string } =
    progress !== null ? { state: "minting", label: t("مینت‌شده", "Minted"), value: withPercent(nf.format(Math.round(progress)), isFa) }
      : phase === "soon" ? { state: "soon", label: t("شروع", "Starts"), value: day(drop.startAt) }
      : phase === "ended" ? { state: "ended", label: t("پایان", "Ended"), value: day(drop.endAt) }
      // Minting, with no published count: its own time instead — when it closes, or since when it is open.
      : phase === "live" && drop.endAt ? { state: "unknown", label: t("پایان مینت", "Closes"), value: day(drop.endAt) }
      : phase === "live" && drop.startAt ? { state: "unknown", label: t("مینت باز از", "Open since"), value: day(drop.startAt) }
      : { state: "unknown", label: t("آمار مینت اعلام نشده", "Mint count not published"), value: "" };
  // Parsed the way the mint gate parses it, so the countdown and the gate agree on when a drop opens.
  const startMs = phase === "soon" ? parseDropDateTime(drop.startAt) : null;
  const countdownTo = startMs !== null && startMs - Date.now() < COUNTDOWN_WINDOW ? new Date(startMs).toISOString() : null;
  const owner = { name: drop.creator?.name, username: drop.creator?.username?.replace(/^@/, ""), avatar: drop.creator?.avatar, blue_check: drop.creator?.verified ? 1 : 0 };

  return (
    <article className="v2-tile v2-drop-tile">
      <Link className="v2-tile-link v2-focus" href={`/${lang}/drops/${drop.slug || drop.id}`} aria-label={drop.title}>
        <span className="v2-sr-only">{drop.title}</span>
      </Link>
      <div className="v2-tile-media">
        <Image src={drop.bannerImage || drop.coverImage || TILE_PLACEHOLDER} alt="" fill loading="lazy" sizes={sizes} style={{ objectFit: "cover" }} />
        {phase ? (
          <span className="v2-live-badge" data-state={phase === "live" ? "live" : "ended"} data-phase={phase}>
            <span className="v2-live-dot" aria-hidden />
            {phase === "live" ? t("در حال مینت", "Minting") : phase === "soldout" ? t("همه مینت شد", "Sold out") : phase === "empty" ? t("بدون آیتم", "No items yet") : phase === "soon" ? t("به‌زودی", "Soon") : t("پایان یافته", "Ended")}
          </span>
        ) : null}
      </div>
      <div className="v2-tile-body">
        <span className="v2-tile-name" title={drop.title}>{drop.title}</span>
        <div className="v2-tile-meta">
          <Creator owner={owner} lang={lang} fallback={t("ناشناس", "Unknown")} />
          {drop.chain ? <span className="v2-chip v2-chip--chain" data-chain={drop.chain.toLowerCase()}>{chainName(drop.chain, isFa)}</span> : null}
        </div>
        <dl className="v2-drop-facts">
          {facts.map((f) => <div key={f.k}><dt>{f.k}</dt><dd>{f.v}{f.unit ? <small>{f.unit}</small> : null}</dd></div>)}
        </dl>
        {/* Every drop ends on the same status line, so a row of tiles lines up
            and none keeps an empty hole where another has its mint bar. */}
        <div className="v2-drop-mint" data-state={foot.state}>
          <span>{foot.label}{countdownTo ? <Countdown iso={countdownTo} isFa={isFa} fallback={foot.value} /> : foot.value ? <b>{foot.value}</b> : null}</span>
          {foot.state === "minting" && progress !== null ? (
            <span className="v2-drop-bar" role="progressbar" aria-valuemin={0} aria-valuemax={fig.supply ?? undefined} aria-valuenow={fig.minted ?? undefined} aria-label={t("پیشرفت مینت", "Mint progress")}>
              <i style={{ inlineSize: `${progress}%` }} />
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
}
